import { getDatabase, ref, get, set, remove, push, update } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { OperatingCost, OperatingCostData, CostInterval } from '../types/operatingCosts';
import { softDeleteFile } from './fileDeletionApi';
import { recordActivity } from './activityApi';
import { FileAttachment } from '../types/attachments';
import { formatDateForStorage } from '../Helpers/DateUtils';

export class OperatingCostsApi {
    private db = getDatabase();

    /**
     * Get the base path for a building's operating costs
     */
    private getCostsPath(buildingKey: string): string {
        return `operatingCosts/${buildingKey}`;
    }

    /**
     * Validate operating cost data
     */
    private validateCostData(data: {
        categoryKey: string;
        amount: number;
        interval: CostInterval;
    }): void {
        if (!data.categoryKey) {
            throw new Error('Kategori krävs');
        }
        if (typeof data.amount !== 'number' || data.amount < 0) {
            throw new Error('Ogiltigt belopp');
        }
        if (!data.interval?.count || data.interval.count < 1) {
            throw new Error('Ogiltigt intervall');
        }
        if (!['month', 'year'].includes(data.interval?.unit)) {
            throw new Error('Ogiltig intervallenhet');
        }
    }

    /**
     * Parse cost data from Firebase snapshot
     */
    private parseCost(data: unknown, key: string): OperatingCost | null {
        if (!data || typeof data !== 'object') return null;
        const d = data as Record<string, unknown>;

        // Validate required fields
        if (typeof d.categoryKey !== 'string' ||
            typeof d.amount !== 'number' ||
            typeof d.createdBy !== 'string' ||
            typeof d.createdAt !== 'string') {
            console.warn(`Invalid operating cost data at ${key}: missing required fields`);
            return null;
        }

        // Validate interval
        const interval = d.interval as Record<string, unknown> | undefined;
        if (!interval || typeof interval.count !== 'number' || typeof interval.unit !== 'string') {
            console.warn(`Invalid interval at ${key}`);
            return null;
        }

        return {
            key,
            categoryKey: d.categoryKey,
            amount: d.amount,
            interval: {
                count: interval.count,
                unit: interval.unit as 'month' | 'year'
            },
            description: typeof d.description === 'string' ? d.description : undefined,
            supplier: typeof d.supplier === 'string' ? d.supplier : undefined,
            attachments: d.attachments as { [key: string]: FileAttachment } | undefined,
            createdBy: d.createdBy,
            createdAt: d.createdAt,
            updatedBy: typeof d.updatedBy === 'string' ? d.updatedBy : undefined,
            updatedAt: typeof d.updatedAt === 'string' ? d.updatedAt : undefined,
        };
    }

    /**
     * Fetch all operating costs for a building
     */
    async getCosts(buildingKey: string): Promise<OperatingCost[]> {
        const costsPath = this.getCostsPath(buildingKey);
        const snapshot = await get(ref(this.db, costsPath));

        if (!snapshot.exists()) {
            return [];
        }

        const data = snapshot.val() as Record<string, unknown>;
        const costs: OperatingCost[] = [];

        for (const [key, value] of Object.entries(data)) {
            const cost = this.parseCost(value, key);
            if (cost) {
                costs.push(cost);
            }
        }

        return costs;
    }

    /**
     * Fetch all operating costs across all buildings
     */
    async getAllCosts(): Promise<{ [buildingKey: string]: OperatingCost[] }> {
        const snapshot = await get(ref(this.db, 'operatingCosts'));

        if (!snapshot.exists()) {
            return {};
        }

        const result: { [buildingKey: string]: OperatingCost[] } = {};
        const data = snapshot.val() as Record<string, Record<string, unknown>>;

        for (const [buildingKey, buildingCosts] of Object.entries(data)) {
            if (!buildingCosts || typeof buildingCosts !== 'object') continue;

            result[buildingKey] = [];
            for (const [costKey, costData] of Object.entries(buildingCosts)) {
                const cost = this.parseCost(costData, costKey);
                if (cost) {
                    result[buildingKey].push(cost);
                }
            }
        }

        return result;
    }

    /**
     * Create a new operating cost
     */
    async createCost(
        buildingKey: string,
        data: {
            categoryKey: string;
            amount: number;
            interval: CostInterval;
            description?: string;
            supplier?: string;
        }
    ): Promise<OperatingCost> {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad för att skapa en driftkostnad');
        }

        this.validateCostData(data);

        const costsPath = this.getCostsPath(buildingKey);
        const newCostRef = push(ref(this.db, costsPath));
        const key = newCostRef.key!;

        const costData: OperatingCostData = {
            categoryKey: data.categoryKey,
            amount: data.amount,
            interval: data.interval,
            createdBy: user.uid,
            createdAt: formatDateForStorage(new Date()),
        };

        // Add optional fields only if provided
        if (data.description?.trim()) {
            costData.description = data.description.trim();
        }
        if (data.supplier?.trim()) {
            costData.supplier = data.supplier.trim();
        }

        await set(newCostRef, costData);
        recordActivity('operatingCost.created', { buildingKey, targetKey: key });

        return {
            key,
            ...costData,
        };
    }

    /**
     * Update an existing operating cost
     */
    async updateCost(
        buildingKey: string,
        costKey: string,
        data: {
            categoryKey: string;
            amount: number;
            interval: CostInterval;
            description?: string;
            supplier?: string;
        }
    ): Promise<void> {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad för att uppdatera en driftkostnad');
        }

        this.validateCostData(data);

        const costPath = `${this.getCostsPath(buildingKey)}/${costKey}`;

        const updates: Partial<OperatingCostData> = {
            categoryKey: data.categoryKey,
            amount: data.amount,
            interval: data.interval,
            updatedBy: user.uid,
            updatedAt: formatDateForStorage(new Date()),
        };

        // Handle optional fields - set to value or null to remove
        if (data.description?.trim()) {
            updates.description = data.description.trim();
        }
        if (data.supplier?.trim()) {
            updates.supplier = data.supplier.trim();
        }

        await update(ref(this.db, costPath), updates);
        recordActivity('operatingCost.updated', { buildingKey, targetKey: costKey });
    }

    /**
     * Delete an operating cost
     */
    async deleteCost(buildingKey: string, costKey: string): Promise<void> {
        const costPath = `${this.getCostsPath(buildingKey)}/${costKey}`;
        await remove(ref(this.db, costPath));
        recordActivity('operatingCost.deleted', { buildingKey, targetKey: costKey });
    }

    /**
     * Add an attachment to an operating cost
     */
    async addAttachment(
        buildingKey: string,
        costKey: string,
        attachment: FileAttachment
    ): Promise<void> {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad för att lägga till en bilaga');
        }

        const attachmentPath = `${this.getCostsPath(buildingKey)}/${costKey}/attachments/${attachment.key}`;
        await set(ref(this.db, attachmentPath), attachment);

        // Update the cost's updatedBy/updatedAt
        const costPath = `${this.getCostsPath(buildingKey)}/${costKey}`;
        await update(ref(this.db, costPath), {
            updatedBy: user.uid,
            updatedAt: formatDateForStorage(new Date()),
        });
    }

    /**
     * Remove an attachment from an operating cost (soft delete — tombstones
     * the metadata and stamps the cost's updatedBy/updatedAt atomically)
     */
    async removeAttachment(
        buildingKey: string,
        costKey: string,
        attachmentKey: string
    ): Promise<void> {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad för att ta bort en bilaga');
        }

        const costPath = `${this.getCostsPath(buildingKey)}/${costKey}`;
        await softDeleteFile({
            originalPath: `${costPath}/attachments/${attachmentKey}`,
            entityType: 'operatingCostAttachment',
            extraUpdates: {
                [`${costPath}/updatedBy`]: user.uid,
                [`${costPath}/updatedAt`]: formatDateForStorage(new Date()),
            },
        });
    }
}

// Export singleton instance
export const operatingCostsApi = new OperatingCostsApi();
